import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiAlertCircle, FiX } from 'react-icons/fi';

const formatCurrency = (value) => new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
}).format(value || 0);

const DetailsModal = ({ row, onClose }) => {
    if (!row) return null;
    const m = row.member || {};

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">{row.memberLabel}</h3>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                    <div>
                        <p className="text-gray-500">Type</p>
                        <p className="font-medium">{row.memberType}</p>
                    </div>
                    {row.memberType === 'SupermanMember' ? (
                        <>
                            <div>
                                <p className="text-gray-500">Name</p>
                                <p className="font-medium">{m.name || '—'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Web ID</p>
                                <p className="font-medium">{m.webID || '—'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Region / Area</p>
                                <p className="font-medium">{[m.region, m.area].filter(Boolean).join(' / ') || '—'}</p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <p className="text-gray-500">God ID</p>
                                <p className="font-medium">{m.godID || '—'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Name</p>
                                <p className="font-medium">{m.godName || m.fullName || '—'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Web ID</p>
                                <p className="font-medium">{m.webID || '—'}</p>
                            </div>
                        </>
                    )}
                </div>

                <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">Package Statistics</h4>
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <p className="text-sm text-gray-500">Total Payments</p>
                            <p className="text-2xl font-bold text-green-600">{formatCurrency(row.totalPayments)}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Packages</p>
                            <p className="text-2xl font-bold text-blue-600">{row.paymentCount}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Reviews Bought</p>
                            <p className="text-2xl font-bold text-purple-600">{row.reviewCount || 0}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SupermanMemberAnalytics = () => {
    const [topMembers, setTopMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                setLoading(true);
                setError('');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/supermanpayments/analytics/top-payments?limit=10`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                            'ngrok-skip-browser-warning': 'true',
                        },
                    }
                );
                if (response.data.success) setTopMembers(response.data.data || []);
            } catch (err) {
                setError(`Failed to fetch member analytics: ${err.response?.data?.error || err.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchAnalytics();
    }, []);

    return (
        <div className="bg-white rounded-lg shadow w-full p-6">
            <h2 className="text-xl font-semibold mb-6">Top Package Members</h2>

            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md flex items-center">
                    <FiAlertCircle className="mr-2" />
                    {error}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center items-center h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
            ) : topMembers.length === 0 ? (
                <p className="text-gray-500">No payment data yet.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Packages</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reviews</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Paid</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {topMembers.map((row, index) => (
                                <tr
                                    key={`${row.memberType}-${row.memberId}`}
                                    className="hover:bg-blue-50 cursor-pointer"
                                    onClick={() => setSelected(row)}
                                >
                                    <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                                    <td className="px-4 py-3 text-sm font-medium text-blue-700">{row.memberLabel}</td>
                                    <td className="px-4 py-3 text-sm">{row.paymentCount}</td>
                                    <td className="px-4 py-3 text-sm">{row.reviewCount || 0}</td>
                                    <td className="px-4 py-3 text-sm font-semibold text-green-700">
                                        {formatCurrency(row.totalPayments)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selected && <DetailsModal row={selected} onClose={() => setSelected(null)} />}
        </div>
    );
};

export default SupermanMemberAnalytics;
